import React from 'react';
import { getImagePath } from '../utils/imageRegistry';

// --- CONFIGURATION (Now Dynamic) ---
const getConstants = (highContrast = false) => ({
    // Dimensions
    cardHeight: 82, 
    cardWidth: 180,
    gap: 40, 
    
    // Style (Changes based on highContrast prop)
    cardBg: highContrast ? '#161824' : '#2B2F3F', 
    cardBorder: highContrast ? '1px solid rgba(255,255,255,0.85)' : '1px solid rgba(255,255,255,0.4)',
    lineColor: highContrast ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)', 
    lineWidth: 2,
    goldColor: '#FFD700',
    winBg: 'rgba(108, 93, 211, 0.9)',

    // Label overrides for the Rules page
    labelOpacity: highContrast ? 1 : 0.5,
    labelColor: highContrast ? '#FFFFFF' : 'inherit',
    labelStyle: highContrast ? 'normal' : 'italic'
});

// --- HELPERS ---

const Spacer = ({ height }) => <div style={{height: height, width: '100%', flexShrink: 0}} />;

const MatchNode = ({ matchId, data, teams, topLabel, bottomLabel, title, isGrandFinal = false, highContrast = false }) => {
    const C = getConstants(highContrast);
    const match = (data && data[matchId]) ? data[matchId] : {};
    const t1 = teams.find(t => t.id.toString() === match.team1?.toString());
    const t2 = teams.find(t => t.id.toString() === match.team2?.toString());

    const TeamRow = ({ team, label, score, isWinner }) => (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0 12px', height: '50%',
            background: isWinner ? C.winBg : 'transparent',
            transition: 'background 0.2s'
        }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden'}}>
                {team?.logoUrl ? (
                   <img src={getImagePath(team.logoUrl)} alt="" style={{width: 20, height: 20, borderRadius: '50%', objectFit: 'cover'}} />
                ) : (
                   <div style={{width: 20, height: 20, borderRadius: '50%', background: team?.color || '#555'}} />
                )}
                <span style={{fontSize: 13, fontWeight: '600', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120}}>
                    {team ? team.name : <span style={{opacity: C.labelOpacity, fontStyle: C.labelStyle, fontSize: 11, color: C.labelColor}}>{label || "TBA"}</span>}
                </span>
            </div>
            {score !== undefined && score !== "" && <span style={{fontWeight: 'bold', fontSize: 13, color: '#fff'}}>{score}</span>}
        </div>
    );

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', gap: 4, position: 'relative',
            transform: isGrandFinal ? 'scale(1.15)' : 'none', transformOrigin: 'left center'
        }}>
            {title && (
                <div style={{
                    fontSize: 10, color: highContrast ? '#E0E0E0' : '#A0AEC0', textTransform: 'uppercase', 
                    textAlign: 'center', letterSpacing: 1, marginBottom: 2, fontWeight: 'bold'
                }}>
                    {title}
                </div>
            )}
            <div style={{
                width: C.cardWidth, 
                height: C.cardHeight,
                background: C.cardBg, 
                border: isGrandFinal ? `2px solid ${C.goldColor}` : C.cardBorder, 
                borderRadius: 8, overflow: 'hidden',
                boxShadow: isGrandFinal ? `0 0 25px ${C.goldColor}50` : '0 4px 6px rgba(0,0,0,0.3)',
                display: 'flex', flexDirection: 'column'
            }}>
                <TeamRow team={t1} label={topLabel} score={match.score1} isWinner={match.winner === 'team1'} />
                <div style={{height: 1, background: 'rgba(255,255,255,0.1)'}}></div>
                <TeamRow team={t2} label={bottomLabel} score={match.score2} isWinner={match.winner === 'team2'} />
            </div>
        </div>
    );
};

const StraightConnector = ({ width = 40, highContrast = false }) => {
    const C = getConstants(highContrast);
    return (
        <div style={{display: 'flex', alignItems: 'center', height: C.cardHeight, width: width}}>
            <div style={{width: '100%', height: C.lineWidth, background: C.lineColor}} />
        </div>
    );
};

const BracketFork = ({ height, highContrast = false }) => {
    const C = getConstants(highContrast);
    return (
        <div style={{display: 'flex', alignItems: 'center', height: height, width: 40}}>
            <div style={{
                width: 20, height: '100%', 
                borderRight: `${C.lineWidth}px solid ${C.lineColor}`, 
                borderTop: `${C.lineWidth}px solid ${C.lineColor}`, 
                borderBottom: `${C.lineWidth}px solid ${C.lineColor}`,
                borderTopRightRadius: 8, borderBottomRightRadius: 8
            }} />
            <div style={{width: 20, height: C.lineWidth, background: C.lineColor}} />
        </div>
    );
};

// --- LAYOUTS ---

const MainBracketLayout = ({ data, teams, highContrast = false }) => {
    const C = getConstants(highContrast);
    const QF_DIST = C.cardHeight + C.gap; 
    const SF_DIST = QF_DIST * 2; 
    const CLAW_DIST = QF_DIST * 3; 
    const SF_OFFSET_TOP = 61;
    const GF_OFFSET_TOP = 183;

    return (
        <div style={{display: 'flex', flexDirection: 'row', padding: '40px 20px', overflowX: 'auto'}}>
            
            {/* COL 1: CLAW-INS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={18} />
                <MatchNode matchId="api1" data={data} teams={teams} title="Claw-in 1" topLabel="RR1 5th" bottomLabel="RR2 4th" highContrast={highContrast} />
                <Spacer height={CLAW_DIST - C.cardHeight*1+7} />
                <MatchNode matchId="api2" data={data} teams={teams} title="Claw-in 2" topLabel="RR1 4th" bottomLabel="RR2 5th" highContrast={highContrast} />
            </div>

            {/* COL 2: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={40} />
                <StraightConnector highContrast={highContrast} />
                <Spacer height={CLAW_DIST - C.cardHeight+29} />
                <StraightConnector highContrast={highContrast} />
            </div>

            {/* COL 3: QUARTER FINALS */}
            <div style={{display: 'flex', flexDirection: 'column', gap: C.gap}}>
                <MatchNode matchId="aqf1" data={data} teams={teams} title="Quarterfinal 1" topLabel="RR1 1st" bottomLabel="Winner CI 1" highContrast={highContrast} />
                <MatchNode matchId="aqf2" data={data} teams={teams} title="Quarterfinal 2" topLabel="RR1 3rd" bottomLabel="RR2 2nd" highContrast={highContrast} />
                <MatchNode matchId="aqf3" data={data} teams={teams} title="Quarterfinal 3" topLabel="RR1 2nd" bottomLabel="RR2 3rd" highContrast={highContrast} />
                <MatchNode matchId="aqf4" data={data} teams={teams} title="Quarterfinal 4" topLabel="Winner CI 2" bottomLabel="RR2 1st" highContrast={highContrast} />
            </div>

            {/* COL 4: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={62} />
                <BracketFork height={QF_DIST+24} highContrast={highContrast} />
                <Spacer height={142} />
                <BracketFork height={QF_DIST+24} highContrast={highContrast} />
            </div>

            {/* COL 5: SEMI FINALS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={SF_OFFSET_TOP+11} />
                <MatchNode matchId="asf1" data={data} teams={teams} title="Semifinal 1" topLabel="Winner QF 1" bottomLabel="Winner QF 2" highContrast={highContrast} />
                <Spacer height={SF_DIST - C.cardHeight+22} />
                <MatchNode matchId="asf2" data={data} teams={teams} title="Semifinal 2" topLabel="Winner QF 3" bottomLabel="Winner QF 4" highContrast={highContrast} />
            </div>

            {/* COL 6: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={134} />
                <BracketFork height={SF_DIST+46} highContrast={highContrast} />
            </div>

            {/* COL 7: FINALS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={GF_OFFSET_TOP+31} />
                <MatchNode matchId="afinal" data={data} teams={teams} title="Grand Final" topLabel="Winner SF 1" bottomLabel="Winner SF 2" isGrandFinal={true} highContrast={highContrast} />
                <Spacer height={80} />
                <div style={{opacity: 0.8, marginLeft: 20}}>
                    <MatchNode matchId="abronze" data={data} teams={teams} title="3rd Place Match" topLabel="Loser SF 1" bottomLabel="Loser SF 2" highContrast={highContrast} />
                </div>
            </div>
        </div>
    );
};

const ConsolationBracketLayout = ({ data, teams, highContrast = false }) => {
    const C = getConstants(highContrast);
    const QF_DIST = C.cardHeight + C.gap; 
    const SF_DIST = QF_DIST * 2; 
    const CLAW_DIST = QF_DIST * 3; 
    const SF_OFFSET_TOP = 61;
    const GF_OFFSET_TOP = 183;

    return (
        <div style={{display: 'flex', flexDirection: 'row', padding: '40px 20px', overflowX: 'auto'}}>
            
            {/* COL 1: CLAW-INS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={18} />
                <MatchNode matchId="bpi1" data={data} teams={teams} title="Claw-in 3" topLabel="RR1 9th" bottomLabel="RR2 8th" highContrast={highContrast} />
                <Spacer height={CLAW_DIST - C.cardHeight*1+7} />
                <MatchNode matchId="bpi2" data={data} teams={teams} title="Claw-in 4" topLabel="RR1 8th" bottomLabel="RR2 9th" highContrast={highContrast} />
            </div>

            {/* COL 2: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={40} />
                <StraightConnector highContrast={highContrast} />
                <Spacer height={CLAW_DIST - C.cardHeight+29} />
                <StraightConnector highContrast={highContrast} />
            </div>

            {/* COL 3: QUARTER FINALS */}
            <div style={{display: 'flex', flexDirection: 'column', gap: C.gap}}>
                <MatchNode matchId="bqf1" data={data} teams={teams} title="Quarterfinal 1" topLabel="Loser CI 1" bottomLabel="Winner CI 3" highContrast={highContrast} />
                <MatchNode matchId="bqf2" data={data} teams={teams} title="Quarterfinal 2" topLabel="RR1 7th" bottomLabel="RR2 6th" highContrast={highContrast} />
                <MatchNode matchId="bqf3" data={data} teams={teams} title="Quarterfinal 3" topLabel="RR1 6th" bottomLabel="RR2 7th" highContrast={highContrast} />
                <MatchNode matchId="bqf4" data={data} teams={teams} title="Quarterfinal 4" topLabel="Winner CI 4" bottomLabel="Loser CI 2" highContrast={highContrast} />
            </div>

            {/* COL 4: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={62} />
                <BracketFork height={QF_DIST+24} highContrast={highContrast} />
                <Spacer height={142} />
                <BracketFork height={QF_DIST+24} highContrast={highContrast} />
            </div>

            {/* COL 5: SEMI FINALS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={SF_OFFSET_TOP+11} />
                <MatchNode matchId="bsf1" data={data} teams={teams} title="Semifinal 1" topLabel="Winner QF 1" bottomLabel="Winner QF 2" highContrast={highContrast} />
                <Spacer height={SF_DIST - C.cardHeight+22} />
                <MatchNode matchId="bsf2" data={data} teams={teams} title="Semifinal 2" topLabel="Winner QF 3" bottomLabel="Winner QF 4" highContrast={highContrast} />
            </div>

            {/* COL 6: CONNECTORS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={134} />
                <BracketFork height={SF_DIST+46} highContrast={highContrast} />
            </div>

            {/* COL 7: FINALS */}
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <Spacer height={GF_OFFSET_TOP+31} />
                <MatchNode matchId="bfinal" data={data} teams={teams} title="Grand Final" topLabel="Winner SF 1" bottomLabel="Winner SF 2" isGrandFinal={true} highContrast={highContrast} />
                <Spacer height={80} />
                <div style={{opacity: 0.8, marginLeft: 20}}>
                    <MatchNode matchId="bbronze" data={data} teams={teams} title="3rd Place Match" topLabel="Loser SF 1" bottomLabel="Loser SF 2" highContrast={highContrast} />
                </div>
            </div>
        </div>
    );
};

const Bracket = ({ type = "main", data = {}, teams = [], highContrast = false }) => {
    const bracketMatches = (data && data[type]) ? data[type] : {}; 
    return (
        <div className="GlassCard" style={{padding: 0, overflow: 'hidden', margin: '20px 0'}}>
            <div className="ScrollArea">
                <div style={{padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                    <h3 style={{margin: 0, color: '#fff'}}>{type === 'main' ? '🏆 Championship KO Bracket' : '🛡️ Challengers KO Bracket'}</h3>
                </div>
                {type === 'main' 
                    ? <MainBracketLayout data={bracketMatches} teams={teams} highContrast={highContrast} /> 
                    : <ConsolationBracketLayout data={bracketMatches} teams={teams} highContrast={highContrast} />
                }
            </div>
        </div>
    );
};

export default Bracket;